import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Validators } from '@angular/forms';
import { ChangePasswordService } from './change-password.service';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { ToastService } from '../../common/services/toast.service';
import { FieldsetModule } from 'primeng/fieldset';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    CardModule,
    MessageModule,
    ReactiveFormsModule,
    FieldsetModule
],
  templateUrl: './change-password.html',
  styleUrl: './change-password.css',
  providers: [ChangePasswordService]
})
export class ChangePasswordComponent {

  loading = signal(false);

  changePasswordFormGroup = new FormGroup({
      currentPassword: new FormControl('',[Validators.required]),
      newPassword: new FormControl('',[Validators.required, this.passwordValidator, Validators.minLength(8)]),
      confirmPassword: new FormControl('',[Validators.required, Validators.minLength(8)])
  }, {validators: this.passwordsMatchValidator});

  passwordState = {
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    hasMinLength: false
  };

   constructor(
    private changePasswordService: ChangePasswordService,
    private toastService: ToastService,
  ) {

  }

  ngOnInit() {
    this.changePasswordFormGroup.get('newPassword')!.valueChanges.subscribe(value => {
      this.passwordState.hasUpper = /[A-Z]/.test(value || "");
      this.passwordState.hasLower = /[a-z]/.test(value || "");
      this.passwordState.hasNumber = /[0-9]/.test(value || "");
      this.passwordState.hasSpecial = /[^A-Za-z0-9]/.test(value || "");
      this.passwordState.hasMinLength = (value?.length || 0) >= 8;
    });
  }

  async onSubmit() {
    this.loading.set(true);

    if(!this.changePasswordFormGroup.valid) {
      this.loading.set(false);
      //TODO: Show error message
      return;
    }

    const currentPassword = this.changePasswordFormGroup.get('currentPassword')?.value || "";
    const newPassword = this.changePasswordFormGroup.get('newPassword')?.value || "";
    
    try {
      const changePasswordResponse = await this.changePasswordService.changePassword({currentPassword, newPassword});

      if(!changePasswordResponse.success) {
        console.error(changePasswordResponse.message);
        this.toastService.show({
          severity: 'error',
          summary: 'Change Password Error',
          detail: changePasswordResponse.message
        });
      }
      else {
        this.toastService.show({
          severity: 'success',
          summary: 'Password Changed',
          detail: changePasswordResponse.message
        });
      }
    }
    catch (error) {
      console.error(error);
      this.toastService.show({
          severity: 'error',
          summary: 'Server Error',
          detail: 'Failed to reach Endpoint'
      });
    }
    finally {
      this.loading.set(false);
    }
  }

  passwordValidator(control: AbstractControl) {
    const value = control.value || '';

    const valid =
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value) &&
      value.length >= 8;

    return valid ? null : { weakPassword: true };
  }

  passwordsMatchValidator(control: AbstractControl) {
      const newPassword = control.get('newPassword')?.value;
      const confirmPassword = control.get('confirmPassword')?.value;

      if (!newPassword || !confirmPassword) {
        return null; // don't validate if fields are empty
      }

      return newPassword === confirmPassword ? null : { passwordsMismatch: true };
  };
}
